import { useQuery, useMutation } from "@tanstack/react-query";
import type { BillingStateResponse } from "@coach/api-client";
import { api } from "./api.js";

/**
 * TanStack Query hook for the org's current billing state.
 *
 * Polls every 60s while the tab is visible so subscription / hardware
 * status updates land in the UI shortly after the webhook fires server-side.
 * The query key is `["billing", "state"]` — invalidate it after any mutation
 * that mutates billing (checkout return, portal close, license refresh).
 */
export function useBillingState() {
  return useQuery({
    queryKey: ["billing", "state"],
    queryFn: () => api().billing.state(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/**
 * Mutation: open the Stripe Customer Portal and follow the redirect.
 * Mutation rather than a plain helper so callers get loading / error state.
 */
export function useOpenBillingPortal() {
  return useMutation({
    mutationFn: async (returnTo?: string) => {
      const { url } = await api().billing.openPortal(
        returnTo ? { returnTo } : undefined,
      );
      window.location.href = url;
    },
  });
}

/**
 * Hard block derived from the Stripe subscription (canceled / unpaid /
 * paused-without-trial). A valid manual license overrides Stripe entirely,
 * so it never hard-blocks — the server only reports `effective.kind ===
 * "manual"` while the manual license is active and in its date range.
 */
export function isBillingHardBlocked(state: BillingStateResponse | undefined): boolean {
  if (state?.effective?.kind === "manual") return false;
  const sub = state?.subscription;
  return (
    sub != null &&
    (sub.status === "canceled" ||
      sub.status === "unpaid" ||
      (sub.status === "paused" && !sub.trialEnd))
  );
}

/**
 * Subscription tier badge / label derived from the billing state. Keeps the
 * mapping in one place so banners and the billing page agree.
 */
export function describeSubscription(state: BillingStateResponse | undefined): {
  label: string;
  tone: "ok" | "trial" | "warn" | "danger" | "muted";
  daysLeft: number | null;
} {
  if (state?.effective?.kind === "manual") {
    return {
      label: "Manuell verwaltet",
      tone: "ok",
      daysLeft: daysUntil(state.effective.expiresAt),
    };
  }
  if (!state?.subscription) {
    if (state?.license?.plan === "trial") {
      const daysLeft = daysUntil(state.license.expiresAt);
      return { label: "Testphase (legacy)", tone: "trial", daysLeft };
    }
    return { label: "Keine Subscription", tone: "muted", daysLeft: null };
  }
  const sub = state.subscription;
  const daysLeft = daysUntil(sub.currentPeriodEnd);
  if (sub.status === "trialing") {
    return {
      label: "Testphase",
      tone: "trial",
      daysLeft: daysUntil(sub.trialEnd),
    };
  }
  if (sub.status === "active") {
    if (sub.cancelAtPeriodEnd) {
      return { label: "Gekündigt — läuft aus", tone: "warn", daysLeft };
    }
    return { label: "Aktiv", tone: "ok", daysLeft };
  }
  if (sub.status === "past_due") {
    return { label: "Zahlung überfällig", tone: "danger", daysLeft };
  }
  if (sub.status === "paused") {
    return {
      label: "Pausiert — Zahlungsmethode fehlt",
      tone: "danger",
      daysLeft,
    };
  }
  if (sub.status === "canceled") {
    return { label: "Gekündigt", tone: "warn", daysLeft };
  }
  return { label: sub.status, tone: "muted", daysLeft };
}

function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.floor(ms / (24 * 60 * 60 * 1000));
}
